import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CURRENCIES } from "@/lib/currency";
import { toast } from "sonner";
import { Trash, FloppyDisk, ImageSquare } from "@phosphor-icons/react";

export default function SettingsPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [branding, setBranding] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [rates, setRates] = useState([]);
  const [newRate, setNewRate] = useState({ base: "USD", quote: "GBP", rate: "" });
  const [accounts, setAccounts] = useState([]);
  const [obDate, setObDate] = useState(new Date().toISOString().slice(0, 10));
  const [obLines, setObLines] = useState({}); // {accountId: {debit, credit}}
  const [closes, setCloses] = useState([]);
  const [closeDate, setCloseDate] = useState(
    new Date().toISOString().slice(0, 4) + "-12-31"
  );

  const load = async () => {
    if (!activeCompany) return;
    const [b, r, a, ob, pc] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/branding`),
      api.get(`/fx-rates`),
      api.get(`/companies/${activeCompany.id}/accounts`),
      api.get(`/companies/${activeCompany.id}/opening-balances`),
      api.get(`/companies/${activeCompany.id}/period-closes`),
    ]);
    setBranding(b.data || {});
    setRates(r.data);
    setAccounts(a.data);
    setCloses(pc.data);
    if (ob.data) {
      setObDate(ob.data.date);
      const map = {};
      ob.data.lines.forEach((l) => {
        map[l.account_id] = { debit: l.debit || "", credit: l.credit || "" };
      });
      setObLines(map);
    } else {
      setObLines({});
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const setField = (k) => (e) =>
    setBranding((b) => ({ ...(b || {}), [k]: e.target.value }));

  const saveBranding = async () => {
    try {
      let logo_file_id = branding.logo_file_id;
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const { data } = await api.post(`/companies/${activeCompany.id}/files`, fd);
        logo_file_id = data.id;
      }
      const payload = {
        legal_name: branding.legal_name || "",
        address: branding.address || "",
        tax_id: branding.tax_id || "",
        phone: branding.phone || "",
        email: branding.email || "",
        bank_details: branding.bank_details || "",
        footer_text: branding.footer_text || "",
        invoice_template: branding.invoice_template || "classic",
        logo_file_id,
      };
      const { data } = await api.patch(`/companies/${activeCompany.id}/branding`, payload);
      setBranding(data);
      setLogoFile(null);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const saveRate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fx-rates", { ...newRate, rate: parseFloat(newRate.rate) });
      toast.success("Rate saved");
      setNewRate({ base: "USD", quote: "GBP", rate: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteRate = async (base, quote) => {
    await api.delete(`/fx-rates/${base}/${quote}`);
    load();
  };

  const setObField = (accId, field, value) => {
    setObLines((cur) => ({
      ...cur,
      [accId]: { ...(cur[accId] || {}), [field]: value },
    }));
  };

  const saveOpeningBalances = async () => {
    const lines = accounts
      .map((a) => {
        const l = obLines[a.id] || {};
        const debit = parseFloat(l.debit) || 0;
        const credit = parseFloat(l.credit) || 0;
        return { account_id: a.id, debit, credit };
      })
      .filter((l) => l.debit || l.credit);
    if (!lines.length) {
      toast.error("Enter at least one opening balance");
      return;
    }
    try {
      await api.post(`/companies/${activeCompany.id}/opening-balances`, {
        as_of_date: obDate,
        lines,
      });
      toast.success("Opening balances posted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const closeYear = async () => {
    if (!window.confirm(
      `Close all revenue & expense accounts to Retained Earnings as of ${closeDate}? This can be reversed but is a formal accounting step.`
    )) return;
    try {
      const { data } = await api.post(`/companies/${activeCompany.id}/year-end-close`, {
        close_date: closeDate,
      });
      toast.success(`Year closed. Net income: ${data.net_income}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const reopenClose = async (pid) => {
    if (!window.confirm("Reopen this period? The closing journal will be reversed.")) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/period-closes/${pid}`);
      toast.success("Period reopened");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const obDebitTotal = accounts.reduce(
    (s, a) => s + (parseFloat(obLines[a.id]?.debit) || 0),
    0
  );
  const obCreditTotal = accounts.reduce(
    (s, a) => s + (parseFloat(obLines[a.id]?.credit) || 0),
    0
  );
  const obDiff = obDebitTotal - obCreditTotal;

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Company Settings"
        description="Letterhead and invoice branding for this company. FX rates are shared across all companies."
      />
      <PageBody>
        <Tabs defaultValue="branding">
          <TabsList className="rounded-sm bg-muted">
            <TabsTrigger value="branding" className="rounded-sm font-mono uppercase text-xs">
              Letterhead & Invoice
            </TabsTrigger>
            <TabsTrigger value="fx" className="rounded-sm font-mono uppercase text-xs">
              FX Rates
            </TabsTrigger>
            <TabsTrigger value="opening" className="rounded-sm font-mono uppercase text-xs">
              Opening Balances
            </TabsTrigger>
            <TabsTrigger value="close" className="rounded-sm font-mono uppercase text-xs">
              Year-End Close
            </TabsTrigger>
          </TabsList>

          <TabsContent value="branding" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="border border-border bg-card p-6 space-y-4 lg:col-span-2">
                <div className="overline">Company Details on Invoice</div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Legal Name</Label>
                  <Input
                    value={branding?.legal_name || ""}
                    onChange={setField("legal_name")}
                    data-testid="branding-legal-name"
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Address</Label>
                  <Textarea
                    value={branding?.address || ""}
                    onChange={setField("address")}
                    rows={3}
                    className="rounded-sm font-mono"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Tax ID</Label>
                    <Input value={branding?.tax_id || ""} onChange={setField("tax_id")} className="rounded-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Phone</Label>
                    <Input value={branding?.phone || ""} onChange={setField("phone")} className="rounded-sm font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-widest">Billing Email</Label>
                    <Input value={branding?.email || ""} onChange={setField("email")} className="rounded-sm font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Bank / Payment Details</Label>
                  <Textarea
                    value={branding?.bank_details || ""}
                    onChange={setField("bank_details")}
                    rows={2}
                    className="rounded-sm font-mono"
                    placeholder="e.g., Sort code · Account no · IBAN"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Footer Text</Label>
                  <Input
                    value={branding?.footer_text || ""}
                    onChange={setField("footer_text")}
                    className="rounded-sm font-mono"
                    placeholder="Thank you for your business."
                  />
                </div>
              </div>

              <div className="border border-border bg-card p-6 space-y-4">
                <div className="overline">Design</div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Invoice Template</Label>
                  <Select
                    value={branding?.invoice_template || "classic"}
                    onValueChange={(v) => setBranding((b) => ({ ...b, invoice_template: v }))}
                  >
                    <SelectTrigger data-testid="branding-template" className="rounded-sm font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="classic" className="font-mono">Classic (serif)</SelectItem>
                      <SelectItem value="modern" className="font-mono">Modern (accent bar)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Logo</Label>
                  <div className="border border-dashed border-border p-4 rounded-sm text-center space-y-2">
                    {branding?.logo_file_id && !logoFile ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${activeCompany.id}/files/${branding.logo_file_id}/download`}
                        alt="logo"
                        className="max-h-20 mx-auto"
                      />
                    ) : logoFile ? (
                      <div className="text-sm font-mono">{logoFile.name}</div>
                    ) : (
                      <ImageSquare size={40} className="mx-auto text-muted-foreground" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      data-testid="branding-logo"
                      onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                      className="rounded-sm font-mono text-xs"
                    />
                  </div>
                </div>
                <Button
                  onClick={saveBranding}
                  data-testid="branding-save"
                  className="w-full rounded-sm font-mono uppercase tracking-wider text-xs h-10"
                >
                  <FloppyDisk size={14} className="mr-2" />
                  Save Branding
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="fx" className="mt-4 space-y-4">
            <div className="border border-border bg-card p-6">
              <div className="overline mb-4">Add / Update Rate</div>
              <form onSubmit={saveRate} className="grid grid-cols-4 gap-3 items-end">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Base</Label>
                  <Select value={newRate.base} onValueChange={(v) => setNewRate({ ...newRate, base: v })}>
                    <SelectTrigger className="rounded-sm font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c} className="font-mono">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Quote</Label>
                  <Select value={newRate.quote} onValueChange={(v) => setNewRate({ ...newRate, quote: v })}>
                    <SelectTrigger className="rounded-sm font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c} className="font-mono">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Rate (1 base = N quote)</Label>
                  <Input
                    type="number"
                    step="0.00000001"
                    value={newRate.rate}
                    onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
                    required
                    data-testid="fx-rate-input"
                    className="rounded-sm font-mono text-right"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="fx-rate-save"
                  className="rounded-sm h-10 font-mono uppercase tracking-wider text-xs"
                >
                  Save
                </Button>
              </form>
            </div>

            <div className="border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left">
                    <Th>Base</Th>
                    <Th>Quote</Th>
                    <Th className="text-right">Rate</Th>
                    <Th>Updated</Th>
                    <Th className="text-right pr-4"></Th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr
                      key={`${r.base}-${r.quote}`}
                      data-testid={`fx-row-${r.base}-${r.quote}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <Td className="num">{r.base}</Td>
                      <Td className="num">{r.quote}</Td>
                      <Td className="text-right num">{Number(r.rate).toLocaleString(undefined, { maximumFractionDigits: 8 })}</Td>
                      <Td className="text-xs text-muted-foreground num">
                        {r.updated_at?.slice(0, 10)}
                      </Td>
                      <Td className="text-right pr-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRate(r.base, r.quote)}
                          className="rounded-sm h-8 text-destructive"
                        >
                          <Trash size={14} />
                        </Button>
                      </Td>
                    </tr>
                  ))}
                  {rates.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                        No FX rates. Add rates above to enable multi-currency consolidation.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="opening" className="mt-4 space-y-4">
            <div className="border border-border bg-card p-6 space-y-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="overline">As-of Date</div>
                  <Input
                    type="date"
                    value={obDate}
                    onChange={(e) => setObDate(e.target.value)}
                    data-testid="opening-date"
                    className="rounded-sm font-mono w-40 mt-1"
                  />
                </div>
                <div className="text-right">
                  <div className="overline">Balance</div>
                  <div
                    className={`font-serif-display text-2xl num ${
                      Math.abs(obDiff) < 0.01 ? "text-primary" : "text-accent-foreground"
                    }`}
                  >
                    Dr {obDebitTotal.toFixed(2)} · Cr {obCreditTotal.toFixed(2)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    Difference auto-plugs to "Opening Balance Equity"
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the current balance of each account as of {obDate}. Enter debit for assets/expenses and credit for liabilities/equity/revenue. LedgerLoom will auto-plug the difference to <span className="font-mono">3900 · Opening Balance Equity</span>.
              </p>
            </div>

            <div className="border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left">
                    <Th>Code</Th>
                    <Th>Account</Th>
                    <Th>Type</Th>
                    <Th className="text-right">Debit</Th>
                    <Th className="text-right pr-4">Credit</Th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-b-0">
                      <Td className="num">{a.code}</Td>
                      <Td>{a.name}</Td>
                      <Td><span className="tag-badge capitalize">{a.type}</span></Td>
                      <Td className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={obLines[a.id]?.debit || ""}
                          onChange={(e) => setObField(a.id, "debit", e.target.value)}
                          data-testid={`ob-debit-${a.code}`}
                          className="rounded-sm font-mono text-right w-32 ml-auto"
                        />
                      </Td>
                      <Td className="text-right pr-4">
                        <Input
                          type="number"
                          step="0.01"
                          value={obLines[a.id]?.credit || ""}
                          onChange={(e) => setObField(a.id, "credit", e.target.value)}
                          data-testid={`ob-credit-${a.code}`}
                          className="rounded-sm font-mono text-right w-32 ml-auto"
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={saveOpeningBalances}
                data-testid="ob-save"
                className="rounded-sm font-mono uppercase tracking-wider text-xs h-10"
              >
                Post Opening Balances
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="close" className="mt-4 space-y-4">
            <div className="border border-border bg-card p-6 space-y-4">
              <div>
                <div className="overline">What year-end close does</div>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                  Zeroes out all revenue and expense accounts and posts the net income (or loss) to <span className="font-mono">Retained Earnings</span>. After closing, no journals can be posted on or before the close date. You can reopen a period if needed.
                </p>
              </div>
              <div className="flex items-end gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Close Date</Label>
                  <Input
                    type="date"
                    value={closeDate}
                    onChange={(e) => setCloseDate(e.target.value)}
                    data-testid="close-date"
                    className="rounded-sm font-mono w-40 mt-1"
                  />
                </div>
                <Button
                  onClick={closeYear}
                  data-testid="close-year-btn"
                  className="rounded-sm h-10 font-mono uppercase tracking-wider text-xs"
                >
                  Close Year
                </Button>
              </div>
            </div>

            <div className="border border-border bg-card">
              <div className="overline p-4 border-b border-border">Closed Periods</div>
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left">
                    <Th>Closed Through</Th>
                    <Th className="text-right">Revenue</Th>
                    <Th className="text-right">Expense</Th>
                    <Th className="text-right">Net Income</Th>
                    <Th>Closed At</Th>
                    <Th className="text-right pr-4"></Th>
                  </tr>
                </thead>
                <tbody>
                  {closes.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-b-0">
                      <Td className="num font-medium">{c.closed_up_to}</Td>
                      <Td className="text-right num">{c.total_revenue?.toLocaleString()}</Td>
                      <Td className="text-right num">{c.total_expense?.toLocaleString()}</Td>
                      <Td className={`text-right num ${c.net_income >= 0 ? "text-primary" : "text-destructive"}`}>
                        {c.net_income?.toLocaleString()}
                      </Td>
                      <Td className="text-xs text-muted-foreground num">{c.closed_at?.slice(0, 10)}</Td>
                      <Td className="text-right pr-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reopenClose(c.id)}
                          data-testid={`reopen-${c.id}`}
                          className="rounded-sm h-8 text-destructive font-mono text-xs uppercase"
                        >
                          Reopen
                        </Button>
                      </Td>
                    </tr>
                  ))}
                  {closes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                        No periods closed yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
